import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Users, Phone, Check } from "lucide-react";
import heroImage from "@/assets/gallery/river-hangout.jpg";

const tiers = [
  { size: "10–25 guests", config: "1–2 private cabanas + in-water picnic table", note: "Family birthdays, small reunions, work happy hours" },
  { size: "25–50 guests", config: "3–5 cabanas clustered together", note: "Wedding parties, company outings, large family reunions" },
  { size: "50–80 guests", config: "6–8 adjacent cabanas + glamping tents", note: "Multi-family reunions, multi-day corporate retreats" },
  { size: "80+ / full buyout", config: "All 22 cabanas + 13 glamping tents", note: "Conferences, full-day private events, weddings — call 877-577-7667" },
];

const GroupBookings = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Corporate & Large Group Bookings on the Guadalupe River | Son's"
        description="Group bookings & full-property buyouts at Son's Guadalupe near New Braunfels: 10 to 80+ guests. Cabanas, BBQ, parking, tubing & private river access."
        canonical="/group-bookings"
        ogImage="/og/home.jpg"
      />
      <Header />
      <main className="pt-24">
        <section className="relative h-[50vh] min-h-[380px]">
          <img src={heroImage} alt="Large group enjoying a corporate event on the Guadalupe River at Son's Guadalupe" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <p className="text-coral-orange font-medium mb-3">Corporate & large groups</p>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
                  Group Bookings on the Guadalupe River
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  Family reunions, corporate retreats, wedding weekends, and full-property buyouts — Son's Guadalupe handles 10 to 80+ guests on a private riverfront basecamp.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              Son's Guadalupe operates 22 private riverside cabanas and 13 luxury bell tents on a single property in New Braunfels, TX. That means a group of 50 people can keep one cluster of cabanas, share BBQ pits and picnic tables, and have everyone tubing the same float — and the float ends right back at your private basecamp.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              For full-day private events or weekends with 80+ guests, we offer property buyouts. Call 877-577-7667 to plan dates, capacity, BBQ catering, and shuttle logistics.
            </p>
          </div>
        </section>

        <section className="section-padding bg-cream">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-8 text-center">Group capacity ladder</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {tiers.map((t) => (
                <div key={t.size} className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-teal" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-charcoal">{t.size}</h3>
                  </div>
                  <p className="text-charcoal/80 font-semibold mb-1">{t.config}</p>
                  <p className="text-charcoal/60 text-sm">{t.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-6">Group amenities & logistics</h2>
            <ul className="space-y-3 text-muted-foreground text-lg">
              {[
                "Private BBQ grill at every cabana — bring your own food or hire a caterer",
                "Picnic tables & in-water covered picnic tables for group seating",
                "Dedicated parking — no shuttle lots, no walking from the highway",
                "Shuttle-assisted tubing for the whole group — float ends at your cabana cluster",
                "Onsite restrooms & private showers in a clean climate-controlled bathhouse",
                "Glamping bell tents for overnight guests (AC, real beds, sleeps 5 each)",
                "Free Wi-Fi on property",
                "Limited daily capacity — your group isn't fighting public crowds",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal flex-shrink-0 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section-padding bg-teal text-white">
          <div className="container-narrow mx-auto px-4 md:px-8 text-center max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Plan your group event</h2>
            <p className="text-white/85 text-lg mb-6">Call our team for custom group quotes, multi-cabana clusters, and full-property buyouts.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" size="xl" asChild>
                <a href="tel:877-577-7667"><Phone className="w-5 h-5 mr-2" />Call 877-577-7667</a>
              </Button>
              <Link to="/contact"><Button variant="hero-outline" size="xl">Send Inquiry</Button></Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GroupBookings;
